import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, CalendarClock, ChevronLeft, ChevronRight, CopyCheck, KeyRound, Loader2, Plus, Send, User, UserX } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { getClients, type Client } from "../../../lib/clients";
import { getDuplicates } from "../../../lib/duplicates";
import {
  Avatar, Btn, EmptyState, MenuField, PageHead, PillTabs, SearchField, StatusPill, Toolbar, ToolbarGap, fmtDate,
  fmtMoney, fmtNum, listCard,
} from "../../ui";
import { PopHost } from "../../shell/Pop";
import { NewClientModal } from "../_shared/NewClientModal";
import { NextStepBadge } from "../_shared/NextStep";
import { PortalBadge } from "../_shared/PortalBadge";
import { ClientRowMenu } from "./ClientRowMenu";

/**
 * Name, kind, added, invoices, revenue, status - the same seven tracks the
 * design uses, so the column heads line up with the rows without either side
 * measuring the other.
 */
const COLS = "minmax(0,2.1fr) 1.1fr 1.1fr .6fr .9fr 1fr 44px";

/**
 * The client list.
 *
 * Rows are pills, not table cells. This screen was the last one still rendering
 * the pre-redesign `<table>`: the shell around it had already moved to the new
 * vocabulary, so the page arrived with the right heading, the right search pill
 * and then a bordered grey table in the middle of it. The weights were the
 * giveaway - a 500-weight column head where the design asks for 800, and a
 * 400-weight client name where every other list in the CRM writes the row's
 * subject at 650.
 */
type StatusTab = "all" | "active" | "inactive";
type KindFilter = "" | "company" | "person";
/** "" is no portal filter at all, which is not the same question as "none". */
type PortalFilter = "" | "linked" | "invited" | "none";

const STATUS_TABS: StatusTab[] = ["all", "active", "inactive"];

/** The design's page size. Small enough that the footer's range means something,
 *  large enough that most operators never reach the second page. */
const PAGE_SIZE = 25;

export function ClientsList({ user }: { user: StaffUser }) {
  const t = useT();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [kind, setKind] = useState<KindFilter>("");
  const [portal, setPortal] = useState<PortalFilter>("");
  const [tab, setTab] = useState<StatusTab>("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showNew, setShowNew] = useState(false);
  const [dueOnly, setDueOnly] = useState(false);
  const canManage = userCanPlatform(user, "manage:crm.clients");
  const nav = useNavigate();
  // How many records look like a second copy of another one.
  //
  // Asked once, on load, and only for the people who could act on the answer.
  // The scan reads both tables whole, which at this size is nothing; when the
  // lead table outgrows that, this badge is the first thing to give up, not the
  // screen behind it. A button with no number would be worse than none: this
  // screen is empty most days, and nobody opens a door that never has anything
  // behind it.
  const [dupes, setDupes] = useState<number | null>(null);

  useEffect(() => {
    if (!canManage) return;
    let alive = true;
    getDuplicates()
      .then((res) => { if (alive) setDupes(res.pairs.length); })
      // A failed count leaves the button plain rather than showing a zero it
      // did not measure.
      .catch(() => {});
    return () => { alive = false; };
  }, [canManage]);

  useEffect(() => {
    const id = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(id);
  }, [qInput]);

  // Any change to what is being asked for starts at the first page. Staying on
  // page 4 of a result set that now has one page shows an empty card and looks
  // like the filter found nothing.
  useEffect(() => {
    setPage(1);
  }, [q, kind, tab, dueOnly, portal]);

  // Every filter is answered by the endpoint rather than by trimming the loaded
  // page: filtering client-side would silently mean "of the first hundred", and
  // a count that shrinks when you pick a filter it never applied is worse than
  // no filter.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClients({
        page,
        pageSize: PAGE_SIZE,
        q: q || undefined,
        kind: kind || undefined,
        status: tab === "all" ? undefined : tab,
        nextStep: dueOnly ? "due" : undefined,
        portal: portal || undefined,
      });
      setClients(res.rows);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [q, kind, tab, dueOnly, portal, page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    // One open menu at a time across every row.
    <PopHost>
      <PageHead
        title={t("clients.title")}
        sub={t("clients.count", { n: total })}
        actions={
          canManage ? (
            <>
              <Btn onClick={() => nav("/clients/duplicates")}>
                <CopyCheck className="size-4" />
                {t("duplicates.title")}
                {dupes ? (
                  <span className="ml-1 rounded-full bg-amber-500/15 px-1.5 text-[11px] font-semibold text-amber-700 tabular-nums dark:text-amber-400">
                    {dupes}
                  </span>
                ) : null}
              </Btn>
              <Btn tone="brand" onClick={() => setShowNew(true)}>
                <Plus className="size-4" />
                {t("clients.add")}
              </Btn>
            </>
          ) : null
        }
      />

      {/* Search and the dropdowns on the left, the status tabs pushed right -
          the toolbar shape every other list page in the design uses. */}
      <Toolbar>
        <SearchField value={qInput} onChange={setQInput} placeholder={t("clients.search")} />
        <MenuField
          value={kind}
          onChange={setKind}
          label={t("clients.type")}
          options={[
            { value: "", label: t("clients.filter.allKinds") },
            { value: "company", label: t("clients.company"), icon: <Building2 className="size-4" /> },
            { value: "person", label: t("clients.person"), icon: <User className="size-4" /> },
          ]}
        />
        {/* The portal lens. A menu rather than a toggle because there are three
            answers and the third one - approached, still waiting - is the one
            somebody has to chase; a two-state switch would hide it. */}
        <MenuField
          value={portal}
          onChange={setPortal}
          label={t("clients.portal.label")}
          options={[
            { value: "", label: t("clients.portal.any") },
            { value: "linked", label: t("clients.portal.linked"), icon: <KeyRound className="size-4" /> },
            { value: "invited", label: t("clients.portal.invited"), icon: <Send className="size-4" /> },
            { value: "none", label: t("clients.portal.none"), icon: <UserX className="size-4" /> },
          ]}
        />
        {/* Both unhappy states at once - nothing planned, and a plan already
            late. The row says which of the two it is. */}
        <button
          type="button"
          aria-pressed={dueOnly}
          onClick={() => setDueOnly((v) => !v)}
          className={`crm-cb crm-cb-wide${dueOnly ? " is-on" : ""}`}
        >
          <CalendarClock className="size-4" />
          {t("nextStep.filter")}
        </button>
        <ToolbarGap />
        <PillTabs
          value={tab}
          onChange={setTab}
          tabs={STATUS_TABS.map((v) => ({ value: v, label: t(`clients.filter.${v}`) }))}
        />
      </Toolbar>

      <div className="crm-card" style={listCard(COLS)}>
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : clients.length === 0 ? (
          <EmptyState icon={User} title={t("clients.empty")} body={t("clients.emptyBody")} />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("clients.col.name")}</span>
              <span>{t("clients.type")}</span>
              <span>{t("clients.col.added")}</span>
              <span>{t("clients.col.invoices")}</span>
              <span>{t("clients.col.revenue")}</span>
              <span>{t("clients.col.status")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {clients.map((c) => (
                <ClientRow
                  key={c.id}
                  client={c}
                  t={t}
                  canManage={canManage}
                  onChanged={(next) => setClients((p) => p.map((x) => (x.id === next.id ? { ...x, ...next } : x)))}
                />
              ))}
            </div>

            <div className="crm-tfoot">
              {t("clients.showing", {
                from: (page - 1) * PAGE_SIZE + 1,
                to: (page - 1) * PAGE_SIZE + clients.length,
                total,
              })}
              <span className="crm-push flex gap-2.5">
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("common.prev")}
                </Btn>
                <Btn small disabled={page * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
                  {t("common.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>

      {showNew ? (
        <NewClientModal onClose={() => setShowNew(false)} onCreate={(c) => setClients((p) => [c, ...p])} />
      ) : null}
    </PopHost>
  );
}

function ClientRow({
  client,
  t,
  canManage,
  onChanged,
}: {
  client: Client;
  t: ReturnType<typeof useT>;
  canManage: boolean;
  onChanged: (next: Client) => void;
}) {
  const Kind = client.kind === "company" ? Building2 : User;

  return (
    // A div, not a link: the row ends in a menu button, and interactive content
    // inside an <a> is invalid HTML and swallows the button's clicks. The name
    // link stretches an ::after over the whole row instead, so the row stays
    // clickable end to end and the button still sits on top of it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        <Avatar name={client.name} />
        <span className="min-w-0">
          <Link
            to={`/clients/${client.id}`}
            className="crm-prow-n crm-trunc block after:absolute after:inset-0 after:rounded-full"
          >
            {client.name}
          </Link>
          <span className="crm-prow-s crm-trunc flex items-center gap-2">
            <span className="crm-trunc">{client.email ?? client.regCode ?? "-"}</span>
            {/* Read-only here. A client's steps are planned on its own page,
                where the timeline that explains them is already on screen; the
                marker's job in a register is to say which rows to open. */}
            {client.status === "active" ? <NextStepBadge nextStepAt={client.nextStepAt} /> : null}
          </span>
        </span>
      </span>

      {/* The design puts a role dropdown here. There is no role on a client -
          the nearest real field is what kind of party it is, so that is what
          this column says rather than inventing one. */}
      <span className="crm-mut flex items-center gap-1.5 text-[12.5px]">
        <Kind className="size-3.5" />
        {t(client.kind === "company" ? "clients.company" : "clients.person")}
      </span>

      <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(client.createdAt)}</span>
      <span className="crm-tnum text-[13px]">{fmtNum(client._count?.invoices ?? 0)}</span>
      {/* Money paid, not money billed - see the note on the list endpoint. */}
      <span className="crm-tnum text-[13.25px] font-bold">{fmtMoney(client.revenueCents ?? 0)}</span>
      {/* Two answers about the same thing - what state is this customer in - so
          they share a cell. The portal marker started next to the e-mail in the
          subline and was measured out of it: that line is 197px wide and the
          next-step chip already takes 140, so the marker either overflowed the
          name column or squeezed the address down to two letters. Here it has
          room, and a column of markers is easier to scan than one buried in
          prose. */}
      <span className="flex items-center gap-1.5">
        <StatusPill tone={client.status === "active" ? "ok" : "mut"}>
          {t(client.status === "active" ? "clients.statusActive" : "clients.statusInactive")}
        </StatusPill>
        <PortalBadge access={client.portal} />
      </span>

      <ClientRowMenu client={client} canManage={canManage} onChanged={onChanged} />
    </div>
  );
}
